import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { SubscriptionLimits } from '@/types/subscription';
import { subscriptionService } from '@/services/api';
import { useAuth } from '@/contexts/auth-context';

interface SubscriptionContextType {
  limits: SubscriptionLimits | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isFeatureEnabled: (feature: keyof SubscriptionLimits['features']) => boolean;
  isAtLimit: (resource: keyof SubscriptionLimits['usage']) => boolean;
  canPerformAction: (resource: keyof SubscriptionLimits['usage']) => boolean;
  getUsagePercentage: (resource: keyof SubscriptionLimits['usage']) => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Fetch subscription limits using React Query
  const {
    data: limits,
    isLoading,
    isError,
    error,
    refetch,
  }: UseQueryResult<SubscriptionLimits, Error> = useQuery({
    queryKey: ['subscription', 'limits'],
    queryFn: () => subscriptionService.getSubscriptionLimits(),
    enabled: isAuthenticated && !authLoading, // Only fetch when authenticated
    staleTime: 0, // Always refetch - subscription can change externally via Stripe portal
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true, // Refetch when user returns from Stripe portal
  });

  /**
   * Check if a specific feature is enabled for the current subscription
   */
  const isFeatureEnabled = useCallback(
    (feature: keyof SubscriptionLimits['features']): boolean => {
      if (!limits) return false;
      return limits.features[feature] === true;
    },
    [limits]
  );

  /**
   * Check if a resource has reached its limit (100%)
   */
  const isAtLimit = useCallback(
    (resource: keyof SubscriptionLimits['usage']): boolean => {
      if (!limits) return false;
      const usage = limits.usage[resource];
      return usage.current >= usage.limit;
    },
    [limits]
  );

  /**
   * Check if an action can be performed (not at limit)
   */
  const canPerformAction = useCallback(
    (resource: keyof SubscriptionLimits['usage']): boolean => {
      return !isAtLimit(resource);
    },
    [isAtLimit]
  );

  /**
   * Get the usage percentage for a specific resource
   */
  const getUsagePercentage = useCallback(
    (resource: keyof SubscriptionLimits['usage']): number => {
      if (!limits) return 0;
      return limits.usage[resource].percentage;
    },
    [limits]
  );

  const value: SubscriptionContextType = {
    limits,
    isLoading,
    isError,
    error,
    refetch: () => {
      refetch();
    },
    isFeatureEnabled,
    isAtLimit,
    canPerformAction,
    getUsagePercentage,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

/**
 * Hook to check if a feature is available before performing an action
 * Provides both the check and helper to show upgrade dialog
 */
export function useFeatureCheck() {
  const { isFeatureEnabled, isAtLimit, canPerformAction } = useSubscription();

  return {
    isFeatureEnabled,
    isAtLimit,
    canPerformAction,
  };
}
